import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Alert,
  Stack,
} from "@material-ui/core";
import { LoadingButton } from "@mui/lab";
import { useDispatch, useSelector } from "src/redux/store";
import PlanPicker from "src/components/PlanPicker";
import { getPlans } from "src/redux/slices/plan";
import { readPendingSelection, savePendingSelection } from "src/utils/pendingSelection";

// Wizard · Step 7 · Plan (elegir/cambiar el plan de suscripción).
// Se pre-selecciona con el plan que el user marcó en la landing
// (`pendingSelection.planId` en localStorage). Pintamos un banner
// destacado para que el user entienda que su elección de la landing
// llegó al portal — muy importante para no perder el contexto entre
// dominios.

export default function StepPlan({ onComplete, onSkip }) {
  const dispatch = useDispatch();
  const { plans } = useSelector((s) => s.plan);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [cycle, setCycle] = useState("monthly");
  const [initialised, setInitialised] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!plans || plans.length === 0) dispatch(getPlans(false));
  }, [dispatch, plans]);

  const pending = useMemo(() => readPendingSelection(), []);
  const camePreselected = !!pending?.planId;

  // Hidratación cuando llegan los planes.
  useEffect(() => {
    if (!plans || plans.length === 0) return;
    if (initialised) return;
    if (pending?.planId) {
      const p = plans.find((x) => x.id === pending.planId);
      if (p) {
        setSelectedPlanId(p.id);
        setCycle(p.isYearly ? "annual" : "monthly");
      } else if (pending?.cycle) {
        setCycle(pending.cycle);
      }
    } else if (pending?.cycle) {
      setCycle(pending.cycle);
    }
    setInitialised(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plans]);

  // Persistencia AUTO en cada cambio de selección — antes solo se
  // guardaba al pulsar "Continuar", así que si el user cambiaba de tab
  // (Extras/Soporte dentro de StepSubscription) o recargaba la página,
  // el plan seleccionado se perdía y en el checkout aparecía "sin plan
  // seleccionado". Ahora sincronizamos localStorage cada vez que
  // selectedPlanId o cycle cambian, independiente del submit.
  useEffect(() => {
    if (!initialised) return;
    if (!selectedPlanId) return;
    const existing = readPendingSelection() || { addOnIds: [] };
    savePendingSelection({
      ...existing,
      planId: selectedPlanId,
      cycle,
      savedAt: new Date().toISOString(),
    });
  }, [initialised, selectedPlanId, cycle]);

  const selectedPlan = plans?.find((p) => p.id === selectedPlanId) || null;

  const handleContinue = () => {
    setSaving(true);
    try {
      const existing = readPendingSelection() || {
        addOnIds: [],
        savedAt: new Date().toISOString(),
      };
      savePendingSelection({
        ...existing,
        planId: selectedPlanId ?? existing.planId,
        cycle,
        savedAt: new Date().toISOString(),
      });
      onComplete?.({
        dataJson: JSON.stringify({ planId: selectedPlanId, cycle }),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
        Confirma tu plan
      </Typography>
      <Typography sx={{ color: "text.secondary", mb: 3 }}>
        Puedes cambiarlo cuando quieras. No se cobra nada durante los 30 días
        de prueba — solo al activar la suscripción desde el portal.
      </Typography>

      {camePreselected && selectedPlan && (
        <Alert severity="success" sx={{ mb: 3 }}>
          🎯 Ya trajiste tu plan seleccionado desde la landing:{" "}
          <strong>{selectedPlan.name}</strong>. Si prefieres otro, cámbialo
          aquí antes de continuar.
        </Alert>
      )}

      {camePreselected && !selectedPlan && plans?.length > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          El plan que elegiste en la landing ya no está disponible. Elige uno
          de los actuales.
        </Alert>
      )}

      <PlanPicker
        value={selectedPlanId}
        onChange={setSelectedPlanId}
        cycle={cycle}
        onCycleChange={setCycle}
      />

      <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 4 }}>
        <Button color="inherit" onClick={onSkip} disabled={saving}>
          Decidir después
        </Button>
        <LoadingButton
          variant="contained"
          loading={saving}
          onClick={handleContinue}
          disabled={!selectedPlanId}
          sx={{ minWidth: 180 }}
        >
          Guardar plan
        </LoadingButton>
      </Stack>
    </Box>
  );
}
